/**
 * Шаг 5 wizard «Результат» (E07.6, ФТ-1.6; E09 цена/ведомость/заявки).
 * layout=intermediate — после параметров одной модели;
 * layout=final — итог подбора (Figma 1:334) для зарегистрированного клиента.
 */

import { useEffect, useState } from "react";
import type {
  AccessType,
  CalcPreviewResult,
  PublicBomPreviewResult,
  PublicDrivePreviewResult,
  PublicPricePreviewResult,
} from "@potok/shared";
import { pickRequiredDrivePowerKw } from "@potok/shared";
import type { PublicCatalogApi } from "../../api/catalog.api";
import { ApiRequestError } from "../../api/http";
import lockIcon from "../../assets/wizard-result-lock.svg";
import lockSmIcon from "../../assets/wizard-result-lock-sm.svg";

export type ResultStepProps = {
  api: PublicCatalogApi;
  projectId: string | null;
  modelId: string;
  unitId: string | null;
  values: Readonly<Record<string, string>>;
  modelOrdinal: number;
  modelTotal: number;
  /** intermediate — одна модель; final — экран «Результат подбора». */
  layout?: "intermediate" | "final";
  /** Клиент уже вошёл — тексты без «зарегистрируйтесь». */
  isRegisteredClient?: boolean;
  onBack: () => void;
  onContinue: (result: CalcPreviewResult) => void;
  onRestart?: () => void;
  onGoCabinet?: () => void;
};

type RequestState = "idle" | "busy" | "sent" | "open" | "error";

export function ResultStep({
  api,
  projectId,
  modelId,
  unitId,
  values,
  modelOrdinal,
  modelTotal,
  layout = "intermediate",
  isRegisteredClient = false,
  onBack,
  onContinue,
  onRestart,
  onGoCabinet,
}: ResultStepProps) {
  const [result, setResult] = useState<CalcPreviewResult | null>(null);
  const [price, setPrice] = useState<PublicPricePreviewResult | null>(null);
  const [bom, setBom] = useState<PublicBomPreviewResult | null>(null);
  const [drive, setDrive] = useState<PublicDrivePreviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestState, setRequestState] = useState<Partial<Record<AccessType, RequestState>>>({});
  const [requestError, setRequestError] = useState<Partial<Record<AccessType, string>>>({});

  const valuesKey = JSON.stringify(values);
  const isFinal = layout === "final";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setResult(null);
    setPrice(null);
    setBom(null);
    setDrive(null);

    const parsed = JSON.parse(valuesKey) as Record<string, string>;

    void (async () => {
      try {
        const loaded = await api.previewCalc({
          ...(projectId === null ? {} : { projectId }),
          modelId,
          unitId,
          values: parsed,
        });
        if (cancelled) {
          return;
        }
        setResult(loaded);

        const requiredPower = pickRequiredDrivePowerKw(loaded.outputs);
        const drivePromise =
          requiredPower === null
            ? Promise.resolve(null)
            : api.previewDrive({ requiredPowerKw: requiredPower, modelId });

        if (projectId !== null && unitId !== null) {
          const [priceResult, bomResult, driveResult] = await Promise.all([
            api.previewPrice({ projectId, unitId }),
            api.previewBom({ projectId, unitId }),
            drivePromise,
          ]);
          if (!cancelled) {
            setPrice(priceResult);
            setBom(bomResult);
            setDrive(driveResult);
          }
        } else {
          const driveResult = await drivePromise;
          if (!cancelled) {
            setDrive(driveResult);
          }
        }
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(describeError(caught));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, projectId, modelId, unitId, valuesKey]);

  async function requestAccess(accessType: AccessType): Promise<void> {
    if (projectId === null) {
      return;
    }
    setRequestState((current) => ({ ...current, [accessType]: "busy" }));
    setRequestError((current) => ({ ...current, [accessType]: undefined }));
    try {
      const resultGrant = await api.requestAccessGrant(projectId, { accessType });
      setRequestState((current) => ({
        ...current,
        [accessType]: resultGrant.alreadyOpen ? "open" : "sent",
      }));
    } catch (caught: unknown) {
      setRequestState((current) => ({ ...current, [accessType]: "error" }));
      setRequestError((current) => ({ ...current, [accessType]: describeError(caught) }));
    }
  }

  if (isFinal) {
    return (
      <section className="wizard-result" aria-labelledby="result-step-title">
        <header className="wizard-result__header">
          <h2 id="result-step-title" className="wizard-result__title">
            Результат подбора
          </h2>
          <p className="wizard-result__subtitle">Шаг 5 из 5 — Предварительный расчёт выполнен</p>
        </header>

        {error ? (
          <p className="wizard-step__status wizard-step__status--error" role="alert">
            {error}
          </p>
        ) : null}
        {loading ? <p className="wizard-step__hint">Выполняется расчёт…</p> : null}

        {!loading && result ? (
          <div className="wizard-result__grid">
            <div className="wizard-result__tech">
              <h3 className="wizard-result__section-title">Технические характеристики</h3>
              <div className="wizard-result-spec">
                <div className="wizard-result-spec__row">
                  <span>Модель конвейера</span>
                  <strong>
                    {[result.modelName, result.unitCode].filter(Boolean).join(" · ")}
                  </strong>
                </div>
                {result.outputs.map((row, index) => (
                  <div
                    key={row.attributeId}
                    className={
                      index % 2 === 0
                        ? "wizard-result-spec__row wizard-result-spec__row--alt"
                        : "wizard-result-spec__row"
                    }
                  >
                    <span>{row.name}</span>
                    <strong>
                      {row.value}
                      {row.unitName ? ` ${row.unitName}` : ""}
                    </strong>
                  </div>
                ))}
              </div>
            </div>

            <div className="wizard-result__aside">
              <div className="wizard-result-price">
                {price?.accessGranted && price.total !== null ? (
                  <>
                    <p className="wizard-result-price__amount">от {formatRub(price.total)}</p>
                    <p className="wizard-result-price__hint">
                      Ориентировочная стоимость базовой комплектации
                    </p>
                  </>
                ) : (
                  <>
                    <p className="wizard-result-price__amount wizard-result-price__amount--muted">
                      Цена по запросу
                    </p>
                    <p className="wizard-result-price__hint">
                      Ориентировочная стоимость базовой комплектации
                    </p>
                    <button
                      type="button"
                      className="wizard-result-price__cta"
                      disabled={
                        projectId === null ||
                        requestState.price === "busy" ||
                        requestState.price === "sent" ||
                        requestState.price === "open"
                      }
                      onClick={() => void requestAccess("price")}
                    >
                      {requestState.price === "sent" || requestState.price === "open"
                        ? "Заявка отправлена"
                        : "Получить детальный расчёт"}
                    </button>
                    <p className="wizard-result-price__note">
                      {isRegisteredClient
                        ? "Доступ к прайсу после проверки"
                        : "Требуется регистрация · Доступ к прайсу после проверки"}
                    </p>
                  </>
                )}
                {requestError.price ? (
                  <p className="wizard-step__status wizard-step__status--error" role="alert">
                    {requestError.price}
                  </p>
                ) : null}
              </div>

              <LockedAccessCard
                title="Комплектовочная ведомость"
                unlocked={Boolean(bom?.accessGranted)}
                hint={
                  isRegisteredClient
                    ? "Доступ по запросу"
                    : "Доступ по запросу · Зарегистрируйтесь"
                }
                state={requestState.bom ?? "idle"}
                error={requestError.bom}
                canRequest={projectId !== null}
                onRequest={() => void requestAccess("bom")}
              />
              <LockedAccessCard
                title="Прайсовая таблица"
                unlocked={Boolean(price?.accessGranted)}
                hint={
                  isRegisteredClient
                    ? "Доступ по запросу"
                    : "Доступ по запросу · Зарегистрируйтесь"
                }
                state={requestState.price ?? "idle"}
                error={requestError.price}
                canRequest={projectId !== null}
                onRequest={() => void requestAccess("price")}
              />
            </div>
          </div>
        ) : null}

        {!loading && bom !== null ? (
          <div className="wizard-result__bom">
            <h3 className="wizard-result__section-title">
              Комплектовочная ведомость (предварительная)
            </h3>
            {bom.accessGranted && bom.items !== null && bom.items.length > 0 ? (
              <BomPreviewTable items={bom.items} price={price} />
            ) : bom.accessGranted && bom.items?.length === 0 ? (
              <p className="wizard-step__hint">В опубликованной ведомости нет позиций.</p>
            ) : (
              <BomPreviewTable
                items={
                  bom.items ??
                  [
                    /* placeholder rows not shown without access — empty locked table header only */
                  ]
                }
                price={null}
                locked
                emptyMessage="Состав ведомости доступен после согласования допуска."
              />
            )}
          </div>
        ) : null}

        {!loading && drive?.drive ? (
          <p className="wizard-step__hint" role="status">
            Привод: {drive.drive.brand} · {drive.drive.designation} · {drive.drive.powerKw} кВт
          </p>
        ) : null}

        <div className="wizard-result__actions">
          <button type="button" className="wizard-result__restart" onClick={onRestart ?? onBack}>
            ← Начать заново
          </button>
          {onGoCabinet ? (
            <button type="button" className="wizard-result__primary" onClick={onGoCabinet}>
              В кабинет
            </button>
          ) : (
            <button
              type="button"
              className="wizard-result__primary"
              disabled={loading || result === null || error !== null}
              onClick={() => {
                if (result) {
                  onContinue(result);
                }
              }}
            >
              Зарегистрироваться и получить доступ
            </button>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="wizard-step" aria-labelledby="result-step-title">
      <h2 id="result-step-title">Промежуточный результат</h2>
      <p className="wizard-step__hint">
        Модель {modelOrdinal} из {modelTotal}
        {result ? ` · ${result.modelName}` : null}
        {result?.unitCode ? ` · ${result.unitCode}` : null}
      </p>

      {error ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="wizard-step__hint">Выполняется расчёт…</p> : null}

      {!loading &&
      result &&
      result.outputs.length === 0 &&
      (result.restrictedOutputs?.length ?? 0) === 0 ? (
        <p className="wizard-step__hint">Нет величин для отображения (show_in_result)</p>
      ) : null}

      {!loading && result && result.outputs.length > 0 ? (
        <table className="result-table">
          <caption className="visually-hidden">Результаты расчёта</caption>
          <thead>
            <tr>
              <th scope="col">Параметр</th>
              <th scope="col">Значение</th>
            </tr>
          </thead>
          <tbody>
            {result.outputs.map((row) => (
              <tr key={row.attributeId}>
                <th scope="row">
                  {row.name}
                  {row.unitName ? (
                    <span className="result-table__unit">, {row.unitName}</span>
                  ) : null}
                </th>
                <td>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {!loading && result && (result.restrictedOutputs?.length ?? 0) > 0 ? (
        <div className="result-restricted" aria-labelledby="result-restricted-title">
          <h3 id="result-restricted-title">Полный тяговый расчёт</h3>
          <p className="wizard-step__hint">
            Эти величины доступны после согласования допуска. Значения на сервере не передаются.
          </p>
          <table className="result-table">
            <caption className="visually-hidden">Параметры с ограниченным доступом</caption>
            <thead>
              <tr>
                <th scope="col">Параметр</th>
                <th scope="col">Доступ</th>
              </tr>
            </thead>
            <tbody>
              {(result.restrictedOutputs ?? []).map((row) => (
                <tr key={row.attributeId}>
                  <th scope="row">
                    {row.name}
                    {row.unitName ? (
                      <span className="result-table__unit">, {row.unitName}</span>
                    ) : null}
                  </th>
                  <td>
                    <span className="result-restricted__hint">доступ по запросу</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <AccessRequestControls
            projectId={projectId}
            accessType="traction_calc"
            state={requestState.traction_calc ?? "idle"}
            error={requestError.traction_calc}
            onRequest={() => void requestAccess("traction_calc")}
          />
        </div>
      ) : null}

      {!loading && price !== null ? (
        <div className="result-price" aria-labelledby="result-price-title">
          <h3 id="result-price-title">Ориентировочная цена</h3>
          {price.accessGranted && price.total !== null ? (
            <p className="result-price__total">
              {formatRub(price.total)}
              <span className="wizard-step__hint"> (с учётом условий клиента)</span>
            </p>
          ) : (
            <>
              <p className="wizard-step__hint">
                Цена доступна после согласования допуска.{" "}
                <span className="result-restricted__hint">доступ по запросу</span>
              </p>
              <AccessRequestControls
                projectId={projectId}
                accessType="price"
                state={requestState.price ?? "idle"}
                error={requestError.price}
                onRequest={() => void requestAccess("price")}
              />
            </>
          )}
        </div>
      ) : null}

      {!loading && bom !== null ? (
        <div className="result-bom" aria-labelledby="result-bom-title">
          <h3 id="result-bom-title">Комплектовочная ведомость</h3>
          {bom.accessGranted && bom.items !== null ? (
            <>
              <p className="wizard-step__hint">
                Версия №{bom.versionNo}
                {bom.totalWeightKg !== null ? ` · масса ${bom.totalWeightKg} кг` : null}
              </p>
              {bom.items.length === 0 ? (
                <p className="wizard-step__hint">В опубликованной ведомости нет позиций.</p>
              ) : (
                <table className="result-table">
                  <caption className="visually-hidden">Позиции комплектовочной ведомости</caption>
                  <thead>
                    <tr>
                      <th scope="col">Раздел</th>
                      <th scope="col">Код</th>
                      <th scope="col">Наименование</th>
                      <th scope="col">Кол-во</th>
                      <th scope="col">Масса</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bom.items.map((row, index) => (
                      <tr key={`${row.section}-${row.componentCode ?? "x"}-${index}`}>
                        <td>{row.section}</td>
                        <td>{row.componentCode ?? "—"}</td>
                        <td>{row.name ?? "—"}</td>
                        <td>{row.qty}</td>
                        <td>
                          {row.weightSource === "unit"
                            ? `${row.unitWeightKg ?? "—"} × ед.`
                            : (row.totalWeightKg ?? "—")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : (
            <>
              <p className="wizard-step__hint">
                Ведомость доступна после согласования допуска.{" "}
                <span className="result-restricted__hint">доступ по запросу</span>
              </p>
              <AccessRequestControls
                projectId={projectId}
                accessType="bom"
                state={requestState.bom ?? "idle"}
                error={requestError.bom}
                onRequest={() => void requestAccess("bom")}
              />
            </>
          )}
        </div>
      ) : null}

      {!loading && drive !== null ? (
        <div className="result-drive" aria-labelledby="result-drive-title">
          <h3 id="result-drive-title">Подбор привода</h3>
          {drive.drive ? (
            <p className="wizard-step__hint" role="status">
              {drive.drive.brand} · {drive.drive.designation} · {drive.drive.powerKw} кВт
              {drive.drive.rpm ? ` · ${drive.drive.rpm} об/мин` : ""}
              {drive.drive.brakeType ? ` · ${drive.drive.brakeType}` : ""}
            </p>
          ) : (
            <p className="wizard-step__hint">Подходящий привод в каталоге не найден.</p>
          )}
        </div>
      ) : null}

      <div className="wizard-step__actions">
        <button type="button" onClick={onBack}>
          Назад к параметрам
        </button>
        <button
          type="button"
          className="wizard-step__primary"
          disabled={loading || result === null || error !== null}
          onClick={() => {
            if (result) {
              onContinue(result);
            }
          }}
        >
          {modelOrdinal < modelTotal ? "Следующая модель" : "Далее"}
        </button>
      </div>
    </section>
  );
}

function LockedAccessCard({
  title,
  unlocked,
  hint,
  state,
  error,
  canRequest,
  onRequest,
}: {
  title: string;
  unlocked: boolean;
  hint: string;
  state: RequestState;
  error: string | undefined;
  canRequest: boolean;
  onRequest: () => void;
}) {
  if (unlocked) {
    return (
      <div className="wizard-result-lock wizard-result-lock--open">
        <p className="wizard-result-lock__title">{title}</p>
        <p className="wizard-result-lock__hint">Доступ открыт</p>
      </div>
    );
  }

  return (
    <div className="wizard-result-lock">
      <img
        src={lockIcon}
        alt=""
        width={16}
        height={18}
        className="wizard-result-lock__icon"
      />
      <div className="wizard-result-lock__body">
        <p className="wizard-result-lock__title">{title}</p>
        <p className="wizard-result-lock__hint">
          {state === "sent" || state === "open" ? "Заявка на рассмотрении" : hint}
        </p>
        {error ? (
          <p className="wizard-step__status wizard-step__status--error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
      {canRequest && state !== "sent" && state !== "open" ? (
        <button
          type="button"
          className="wizard-result-lock__request"
          disabled={state === "busy"}
          onClick={onRequest}
        >
          Запросить
        </button>
      ) : null}
    </div>
  );
}

function BomPreviewTable({
  items,
  price,
  locked = false,
  emptyMessage,
}: {
  items: NonNullable<PublicBomPreviewResult["items"]>;
  price: PublicPricePreviewResult | null;
  locked?: boolean;
  emptyMessage?: string;
}) {
  const priceLocked = locked || !price?.accessGranted;
  const lines = price?.lines ?? [];

  if (items.length === 0) {
    return (
      <div className="wizard-result-bom-table">
        <div className="wizard-result-bom-table__head" role="row">
          <span>№</span>
          <span>Наименование</span>
          <span>Кол-во</span>
          <span>Ед.</span>
          <span>Цена</span>
        </div>
        <p className="wizard-result-bom-table__empty">{emptyMessage ?? "Нет позиций"}</p>
      </div>
    );
  }

  return (
    <div className="wizard-result-bom-table" role="table" aria-label="Комплектовочная ведомость">
      <div className="wizard-result-bom-table__head" role="row">
        <span role="columnheader">№</span>
        <span role="columnheader">Наименование</span>
        <span role="columnheader">Кол-во</span>
        <span role="columnheader">Ед.</span>
        <span role="columnheader">Цена</span>
      </div>
      {items.map((row, index) => {
        const line = lines.find(
          (item) =>
            (row.componentCode && item.componentCode === row.componentCode) ||
            (row.name && item.name === row.name),
        );
        return (
          <div
            key={`${row.section}-${row.componentCode ?? "x"}-${index}`}
            className={
              index % 2 === 1
                ? "wizard-result-bom-table__row wizard-result-bom-table__row--alt"
                : "wizard-result-bom-table__row"
            }
            role="row"
          >
            <span role="cell">{index + 1}</span>
            <span role="cell">{row.name ?? row.componentCode ?? "—"}</span>
            <span role="cell">{row.qty}</span>
            <span role="cell">шт.</span>
            <span className="wizard-result-bom-table__price" role="cell">
              {priceLocked || !line ? (
                <>
                  <img src={lockSmIcon} alt="" width={12} height={9} />
                  <span>—</span>
                </>
              ) : (
                formatRub(line.lineTotal)
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function AccessRequestControls({
  projectId,
  accessType,
  state,
  error,
  onRequest,
}: {
  projectId: string | null;
  accessType: AccessType;
  state: RequestState;
  error: string | undefined;
  onRequest: () => void;
}) {
  if (projectId === null) {
    return (
      <p className="wizard-step__hint">Чтобы запросить доступ, сначала зарегистрируйтесь.</p>
    );
  }

  if (state === "sent") {
    return <p className="wizard-step__hint" role="status">Заявка отправлена на согласование.</p>;
  }
  if (state === "open") {
    return (
      <p className="wizard-step__hint" role="status">
        Заявка уже на рассмотрении или допуск уже выдан.
      </p>
    );
  }

  return (
    <div className="result-access-request">
      {error ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          {error}
        </p>
      ) : null}
      <button type="button" disabled={state === "busy"} onClick={onRequest}>
        {state === "busy" ? "Отправка…" : labelFor(accessType)}
      </button>
    </div>
  );
}

function labelFor(accessType: AccessType): string {
  switch (accessType) {
    case "price":
      return "Запросить доступ к цене";
    case "bom":
      return "Запросить доступ к ведомости";
    case "traction_calc":
      return "Запросить полный тяговый расчёт";
    default:
      return "Запросить доступ";
  }
}

function formatRub(value: string): string {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return `${value} ₽`;
  }
  return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(num)} ₽`;
}

function describeError(caught: unknown): string {
  if (caught instanceof ApiRequestError) {
    return caught.message;
  }
  if (caught instanceof Error) {
    return caught.message;
  }
  return "Не удалось выполнить расчёт";
}
